import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Download, X } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { recordPwaDismissal, isPwaDismissed } from "@/hooks/usePwaInstall";

interface PwaInstallBannerProps {
    canInstall: boolean;
    onInstallClick: () => void;
}

export const PwaInstallBanner: React.FC<PwaInstallBannerProps> = ({
    canInstall,
    onInstallClick,
}) => {
    const { t } = useTranslation();
    const shouldReduceMotion = useReducedMotion();
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (!canInstall || isPwaDismissed()) {
            return;
        }

        // Delay showing banner slightly so initial page load completes
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 1500);

        return () => clearTimeout(timer);
    }, [canInstall]);

    const handleDismiss = () => {
        setIsVisible(false);
        recordPwaDismissal();
    };

    const handleInstall = () => {
        setIsVisible(false);
        recordPwaDismissal();
        onInstallClick();
    };

    const showBanner = canInstall && isVisible;

    return (
        <AnimatePresence>
            {showBanner && (
                <motion.div
                    role="region"
                    aria-label={t("pwa.bannerAriaLabel")}
                    initial={shouldReduceMotion ? false : { y: 60, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={shouldReduceMotion ? undefined : { y: 60, opacity: 0 }}
                    transition={{ type: "spring", damping: 25, stiffness: 260 }}
                    className="fixed bottom-[calc(4.5rem+max(0.5rem,env(safe-area-inset-bottom,0px)))] left-3 right-3 sm:left-auto sm:right-4 sm:w-96 z-40 bg-white/95 dark:bg-warm-dark-surface/95 backdrop-blur-md rounded-2xl border border-warm-border dark:border-warm-dark-border shadow-xl p-3 flex items-center justify-between gap-3 md:hidden"
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2.5 rounded-xl bg-primary/10 text-primary dark:text-green-400 shrink-0">
                            <Download size={20} aria-hidden={true} />
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs font-bold text-secondary dark:text-gray-100 truncate">
                                {t("pwa.bannerTitle")}
                            </p>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                {t("pwa.bannerDesc")}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                        <button
                            type="button"
                            onClick={handleInstall}
                            className="px-3 py-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold transition-all shadow-xs"
                        >
                            {t("pwa.installButton")}
                        </button>
                        <button
                            type="button"
                            onClick={handleDismiss}
                            className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                            aria-label={t("pwa.dismiss")}
                        >
                            <X size={16} aria-hidden={true} />
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default PwaInstallBanner;
